import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { confirmConsultationPayment } from "../api/consultations";
import logger from "../utils/logger.js";

export default function ConsultationPaymentSuccess() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function confirm() {
      const sessionId = searchParams.get("session_id");
      const token = searchParams.get("token");
      const provider = (searchParams.get("provider") || (token && !sessionId ? "paypal" : "stripe")).toLowerCase();
      const mode = searchParams.get("mode") || "";

      if (!sessionId && !token) {
        setError(isAr ? "معرّف جلسة الدفع غير موجود." : "Payment session ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const data = await confirmConsultationPayment({ sessionId, token, provider, mode });
        if (!active) return;
        setResult(data);
      } catch (err) {
        logger.error("Consultation payment success error", err);
        if (!active) return;
        setError(err?.message || (isAr ? "تعذر تأكيد الدفع." : "Unable to confirm payment."));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    confirm();

    return () => {
      active = false;
    };
  }, [searchParams, isAr]);

  return (
    <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="row justify-content-center">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4 p-lg-5 text-center">
              {loading ? (
                <>
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "جارِ تأكيد الدفع..." : "Confirming payment..."}
                  </h1>
                </>
              ) : error ? (
                <>
                  <div className="display-6 mb-3">!</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تعذر تأكيد الدفع" : "Payment confirmation failed"}
                  </h1>
                  <p className="text-muted mb-4">{error}</p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <Link to="/consulting" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "العودة للاستشارات" : "Back to Consulting"}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="display-6 mb-3">✓</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تم تأكيد الحجز والدفع" : "Payment and booking confirmed"}
                  </h1>
                  <p className="text-muted mb-4">
                    {isAr
                      ? "تم إنشاء حجز الاستشارة بنجاح، ويمكنك متابعة التفاصيل من صفحة استشاراتي."
                      : "Your consultation booking was created successfully, and you can track it from My Consultations."}
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill px-4"
                      onClick={() => {
                        const ref = result?.consultation_slug || result?.consultation_id;
                        navigate(ref ? `/consulting/${ref}` : '/consulting');
                      }}
                    >
                      {isAr ? "عرض الاستشارة" : "View Consultation"}
                    </button>
                    <Link to="/my-consultations" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "استشاراتي" : "My Consultations"}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
